import { supabase } from './supabase.ts';

/**
 * Client for the admin API namespace.
 *
 * Only for work the browser cannot do itself: anything needing the service-role
 * key, a KV write, or an R2 binding. Ordinary reads and writes must NOT come
 * through here -- they go straight to PostgREST under RLS, which is both faster
 * and the reason the admin is a SPA at all.
 */

const API_URL = import.meta.env.VITE_API_URL;

/**
 * A custom domain, as both the attach and the check route answer.
 *
 * `status` is the row's own state. `cfStatus` and `sslStatus` are Cloudflare's
 * two independent machines -- ownership and certificate -- and the domain is
 * live only when both of those are active.
 */
export interface DomainState {
  id: string;
  hostname: string;
  status: 'pending' | 'verifying' | 'active' | 'failed';
  cfHostnameId: string;
  cfStatus: string;
  sslStatus: string | null;
  records: Array<{ kind: 'ownership' | 'certificate'; type: string; name: string; value: string }>;
  errors: string[];
}

export interface ApiKeyRecord {
  id: string;
  accountId: string;
  name: string;
  scopes: string[];
  keyPrefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface TeamMember {
  userId: string;
  accountId: string;
  role: string;
  roleHierarchyLevel: number;
  isPrimaryOwner: boolean;
  name: string | null;
  email: string | null;
  pictureUrl: string | null;
  firstName: string | null;
  lastName: string | null;
  badgeNumber: string | null;
  driversLicenseNumber: string | null;
  driversLicenseExpiresAt: string | null;
  iconColor: string | null;
  resources: string[];
  createdAt: string;
}

export interface TeamInvitation {
  id: number;
  email: string;
  role: string;
  resources: string[];
  firstName: string | null;
  lastName: string | null;
  invitedBy: string;
  inviterName: string | null;
  inviterEmail: string | null;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
}

export interface TeamSnapshot {
  members: TeamMember[];
  invitations: TeamInvitation[];
  /** Only the roles the caller may hand out, ranked most elevated first. */
  roles: string[];
}

export interface InviteMemberInput {
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  badgeNumber?: string;
  driversLicenseNumber?: string;
  driversLicenseExpiresAt?: string;
  iconColor?: string;
  resources: string[];
}

/** Endpoints that also report whether the invitation email actually went out. */
interface DispatchResult<T> {
  data: T;
  emailSent: boolean;
}

/**
 * A refused request, with the body kept.
 *
 * Most callers only want `message`. A few need the status or the payload --
 * a 409 on order placement carries the id of the order that already exists,
 * and the right response to that is a redirect, not a toast.
 */
export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(typeof body.error === 'string' ? body.error : `Request failed (${status})`);
    this.name = 'AdminApiError';
  }
}

async function send(path: string, init: RequestInit = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
  });

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    throw new AdminApiError(response.status, body);
  }

  return body;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const body = await send(path, init);

  return body.data as T;
}

/** For the invitation endpoints, which carry `emailSent` alongside `data`. */
async function requestWithDispatch<T>(
  path: string,
  init: RequestInit = {},
): Promise<DispatchResult<T>> {
  const body = await send(path, init);

  return { data: body.data as T, emailSent: body.emailSent === true };
}

/* ============================================================
 * Orders. See `apps/api/src/routes/orders.ts` for why each of these is on
 * the API rather than a PostgREST write.
 * ============================================================ */

export interface OrderProductResult {
  variantId: string;
  productId: string | null;
  name: string;
  variantName: string | null;
  brandName: string | null;
  imageUrl: string | null;
  isCannabis: boolean;
  unitPriceCents: number;
}

export interface OrderDeliverySlot {
  startsAt: string;
  endsAt: string;
  remaining: number | null;
}

export interface OrderBasketInput {
  customerId: string | null;
  locationId: string | null;
  customerType: string;
  fulfillmentType: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    instructions: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  lines: { variantId: string; quantity: number }[];
  promoCode: string | null;
  manualDiscountCents: number;
  manualDiscountNote: string | null;
  tipCents: number;
  paymentProvider: string | null;
  orderId?: string | null;
}

export interface OrderPlaceInput extends OrderBasketInput {
  customerId: string;
  idempotencyKey: string;
  contact: { firstName: string; lastName: string; email: string; phone: string };
  windowStart: string | null;
  windowEnd: string | null;
  notes: string | null;
}

export interface OrderQuote {
  zone: { id: string; name: string } | null;
  lines: {
    variantId: string;
    name: string;
    variantName: string | null;
    brandName: string | null;
    imageUrl: string | null;
    isCannabis: boolean;
    unitPriceCents: number;
    quantity: number;
  }[];
  missingVariantIds: string[];
  fees: { kind: string; name: string; amountCents: number; taxable: boolean }[];
  belowMinimum: { minimumCents: number; shortfallCents: number } | null;
  promo: { code: string; description: string; amountCents: number } | null;
  promoError: string | null;
  manualDiscountCents: number;
  loyalty: { points: number; cents: number };
  pricing: {
    subtotalCents: number;
    discountCents: number;
    feeTotalCents: number;
    taxCents: number;
    includedTaxCents: number;
    tipCents: number;
    totalCents: number;
    taxLines: { kind: string; name: string; rateBps: number; amountCents: number }[];
    pricesInclude: { cannabis: boolean; excise: boolean; sales: boolean };
  };
}

export interface GeocodeSuggestion {
  id: string;
  name: string;
  placeFormatted: string;
}

export interface GeocodeAddress {
  id: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  latitude: number;
  longitude: number;
  placeName: string;
}

export interface RouteLeg {
  stopId: string;
  taskId: string;
  durationSeconds: number;
  distanceMeters: number;
  eta: string;
}

export interface RouteDirections {
  geometry: { type: 'LineString'; coordinates: number[][] };
  legs: RouteLeg[];
  durationSeconds: number;
  distanceMeters: number;
}

export const adminApi = {
  /**
   * Dispatch. Assign and reorder go straight to the database as RPCs (see
   * `lib/deliveries.ts`); these are the calls that need the server -- cash
   * settlement on completion, and the Mapbox token for routing.
   */
  deliveries: {
    setStatus: (
      accountId: string,
      taskId: string,
      input: {
        status: 'assigned' | 'started' | 'arrived' | 'completed' | 'failed';
        reason?: string;
      },
    ) =>
      request<{ orderId: string | null; settledCash: boolean }>(
        `/admin/accounts/${accountId}/deliveries/${taskId}/status`,
        { method: 'POST', body: JSON.stringify(input) },
      ),

    /** Null when the route has no origin or no remaining stops. */
    directions: (accountId: string, routePlanId: string) =>
      request<RouteDirections | null>(`/admin/accounts/${accountId}/routing/directions`, {
        method: 'POST',
        body: JSON.stringify({ routePlanId }),
      }),

    optimize: (accountId: string, routePlanId: string) =>
      request<RouteDirections & { orderedStopIds: string[] }>(
        `/admin/accounts/${accountId}/routing/optimize`,
        { method: 'POST', body: JSON.stringify({ routePlanId }) },
      ),
  },

  orders: {
    searchProducts: (
      accountId: string,
      params: { q: string; locationId: string | null; customerType: string },
    ) => {
      const query = new URLSearchParams({ q: params.q, customerType: params.customerType });
      if (params.locationId) query.set('locationId', params.locationId);

      return request<OrderProductResult[]>(`/admin/accounts/${accountId}/orders/products?${query}`);
    },

    resolveDelivery: (
      accountId: string,
      input: { latitude: number | null; longitude: number | null; zip: string; days?: number },
    ) =>
      request<{ zone: { id: string; name: string } | null; slots: OrderDeliverySlot[] }>(
        `/admin/accounts/${accountId}/orders/resolve-delivery`,
        { method: 'POST', body: JSON.stringify(input) },
      ),

    quote: (accountId: string, input: OrderBasketInput) =>
      request<OrderQuote>(`/admin/accounts/${accountId}/orders/quote`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /** Throws `AdminApiError` 409 with `body.orderId` for a duplicate attempt. */
    place: (accountId: string, input: OrderPlaceInput) =>
      request<{ orderId: string; orderNumber: number; totalCents: number }>(
        `/admin/accounts/${accountId}/orders`,
        { method: 'POST', body: JSON.stringify(input) },
      ),

    editItems: (
      accountId: string,
      orderId: string,
      lines: { variantId: string; quantity: number }[],
    ) =>
      request<{ totalCents: number }>(`/admin/accounts/${accountId}/orders/${orderId}/items`, {
        method: 'PATCH',
        body: JSON.stringify({ lines }),
      }),

    cancel: (accountId: string, orderId: string, reason: string) =>
      request<{ releasedLines: number }>(`/admin/accounts/${accountId}/orders/${orderId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    refund: (accountId: string, orderId: string, input: { amountCents: number; reason: string }) =>
      request<{
        transactionId: string;
        amountCents: number;
        paymentStatus: string;
        cashRecorded: boolean;
      }>(`/admin/accounts/${accountId}/orders/${orderId}/refund`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    assignDriver: (
      accountId: string,
      orderId: string,
      input: { driverId: string | null } | { memberUserId: string },
    ) =>
      request<{ driverId: string | null; taskId: string | null; routePlanId: string | null }>(
        `/admin/accounts/${accountId}/orders/${orderId}/assign-driver`,
        { method: 'POST', body: JSON.stringify(input) },
      ),

    markPackaged: (accountId: string, orderId: string, userId: string) =>
      request<{ packagedBy: { userId: string; name: string; badge: string | null } }>(
        `/admin/accounts/${accountId}/orders/${orderId}/packaged`,
        { method: 'POST', body: JSON.stringify({ userId }) },
      ),

    markDelivered: (accountId: string, orderId: string, userId: string) =>
      request<{ settledCash: boolean }>(
        `/admin/accounts/${accountId}/orders/${orderId}/delivered`,
        { method: 'POST', body: JSON.stringify({ userId }) },
      ),
  },

  /**
   * Address search, through the API so the Mapbox token never reaches the
   * browser. A 503 means no token is configured; callers fall back to typed
   * addresses.
   */
  imports: {
    /**
     * The per-row trail for one import.
     *
     * `import_records` has a read policy and no write one, so these go
     * through the API's service-role client rather than PostgREST.
     */
    records: (
      accountId: string,
      input: {
        jobId: string;
        records: Array<{
          entityType: string;
          externalId: string;
          entityId: string | null;
          action: 'created' | 'updated' | 'skipped' | 'failed';
          error: string | null;
        }>;
      },
    ) =>
      request<{ written: number }>(`/admin/accounts/${accountId}/imports/records`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  },

  geocode: {
    search: (params: { q: string; sessionToken: string; proximity?: string }) => {
      const query = new URLSearchParams({ q: params.q, session_token: params.sessionToken });
      if (params.proximity) query.set('proximity', params.proximity);

      return request<GeocodeSuggestion[]>(`/admin/geocode/search?${query}`);
    },

    retrieve: (params: { id: string; sessionToken: string }) => {
      const query = new URLSearchParams({ id: params.id, session_token: params.sessionToken });

      return request<GeocodeAddress>(`/admin/geocode/retrieve?${query}`);
    },
  },

  /**
   * Domains. Only the two writes that change which hostnames resolve live
   * here -- claiming and deleting both have to update the host -> tenant map
   * in KV, which a browser cannot write. Availability checks, the apex and the
   * primary toggle stay on PostgREST.
   */
  domains: {
    claimSubdomain: (accountId: string, subdomain: string) =>
      request<{ domain: { hostname: string } | null; hostnames: string[]; minted: boolean }>(
        `/admin/accounts/${accountId}/domains/subdomain`,
        { method: 'POST', body: JSON.stringify({ subdomain }) },
      ),

    addCustom: (accountId: string, hostname: string) =>
      request<DomainState>(`/admin/accounts/${accountId}/domains`, {
        method: 'POST',
        body: JSON.stringify({ hostname }),
      }),

    /** Re-reads Cloudflare; binds the hostname once both halves are active. */
    check: (accountId: string, domainId: string) =>
      request<DomainState>(`/admin/accounts/${accountId}/domains/${domainId}/check`),

    remove: (accountId: string, domainId: string) =>
      request<{ deleted: boolean }>(`/admin/accounts/${accountId}/domains/${domainId}`, {
        method: 'DELETE',
      }),
  },

  apiKeys: {
    list: (accountId: string) => request<ApiKeyRecord[]>(`/admin/accounts/${accountId}/api-keys`),

    /**
     * Returns the plaintext key exactly once -- it is not recoverable
     * afterwards, so the caller must surface it immediately.
     */
    async mint(accountId: string, input: { name: string; scopes: string[] }) {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      const response = await fetch(`${API_URL}/admin/accounts/${accountId}/api-keys`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const body = (await response.json()) as {
        data: ApiKeyRecord;
        key: string;
        error?: string;
      };

      if (!response.ok) throw new Error(body.error ?? 'Failed to mint key');

      return body;
    },

    revoke: (accountId: string, keyId: string) =>
      request<{ revoked: boolean }>(`/admin/accounts/${accountId}/api-keys/${keyId}`, {
        method: 'DELETE',
      }),
  },

  support: {
    /**
     * Reply to a shopper.
     *
     * On the API because `support_messages` is read-only to members: the
     * reply and the conversation's own last-message columns have to move
     * together, or the inbox lies about what is waiting.
     */
    reply: (accountId: string, conversationId: string, body: string) =>
      request<{ id: string; created_at: string }>(
        `/admin/accounts/${accountId}/support/conversations/${conversationId}/messages`,
        { method: 'POST', body: JSON.stringify({ body }) },
      ),
  },

  pos: {
    /**
     * Ring up a cash sale.
     *
     * The total is recomputed on the server before any change is handed
     * back, so an under-tender is refused with the real price rather than
     * the one the screen believed.
     */
    sell: (
      accountId: string,
      input: {
        queueEntryId: string | null;
        customerId: string;
        customerType: string;
        locationId: string | null;
        registerSessionId: string | null;
        lines: { variantId: string; quantity: number }[];
        promoCode: string | null;
        manualDiscountCents: number;
        manualDiscountNote: string | null;
        tenderedCents: number;
        idempotencyKey: string;
      },
    ) =>
      request<{
        orderId: string;
        orderNumber: number;
        totalCents: number;
        changeCents: number;
        cashRecorded: boolean;
      }>(`/admin/accounts/${accountId}/pos/sales`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /**
     * Email the receipt for an order.
     *
     * `sent: false` means the customer has no address on file or no mail
     * provider is configured -- neither is an error worth failing a sale
     * that has already been paid for.
     */
    emailReceipt: (accountId: string, orderId: string) =>
      request<{ sent: boolean; to: string | null }>(
        `/admin/accounts/${accountId}/pos/receipts/${orderId}`,
        { method: 'POST' },
      ),
  },

  designs: {
    /**
     * Make one design the live one.
     *
     * On the API because `storefront_sites.published` is not in the browser's
     * UPDATE grant: publishing has to clear the flag on the current design and
     * set it on the new one together, and a unique index refuses two.
     */
    publish: (accountId: string, siteId: string) =>
      request<{ id: string; name: string }>(
        `/admin/accounts/${accountId}/designs/${siteId}/publish`,
        { method: 'POST' },
      ),
  },

  pages: {
    /** Publishes the draft and primes the storefront's KV cache. */
    publish: (accountId: string, pageId: string) =>
      request<{ id: string; slug: string }>(
        `/admin/accounts/${accountId}/pages/${pageId}/publish`,
        { method: 'POST' },
      ),
  },

  media: {
    /**
     * Upload one image to R2.
     *
     * Goes through the API because R2 is a Worker binding with no browser
     * client, and presigned URLs would need an S3 credential the browser must
     * never hold. Returns the object key and the URL to store on the record.
     */
    async upload(accountId: string, file: File, folder = 'products') {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const body = new FormData();
      body.set('file', file);
      body.set('folder', folder);

      // No content-type header: the browser must set the multipart boundary.
      const response = await fetch(`${API_URL}/admin/accounts/${accountId}/media`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body,
      });

      const payload = (await response.json().catch(() => ({}))) as {
        data?: { key: string; url: string };
        error?: string;
      };

      if (!response.ok || !payload.data) {
        throw new Error(payload.error ?? `Upload failed (${response.status})`);
      }

      return payload.data;
    },

    /** Removes the object. The key must be under the caller's own account. */
    remove: (accountId: string, key: string) =>
      request<{ deleted: boolean }>(`/admin/accounts/${accountId}/media`, {
        method: 'DELETE',
        body: JSON.stringify({ key }),
      }),
  },

  inventory: {
    /**
     * Apply a manual stock movement.
     *
     * Not a direct PostgREST write: `inventory_move` is service-role only, so
     * the ledger and the running total are written together and cannot drift.
     * The low-stock threshold is a plain column and is written straight from
     * the browser instead — see `lib/collections.ts`.
     */
    adjust: (
      accountId: string,
      levelId: number,
      input: { quantityDelta: number; reason: string; note: string },
    ) =>
      request<{ id: number; onHand: number; reserved: number; available: number }>(
        `/admin/accounts/${accountId}/inventory/${levelId}/adjust`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
  },

  /**
   * Team management.
   *
   * On the API rather than straight to PostgREST because sending the email
   * needs the Resend key, signing the accept link needs the link key, and the
   * invite token must never reach a browser. See `apps/api/src/routes/team.ts`.
   */
  team: {
    /** Members, pending invitations and assignable roles in one response. */
    get: (accountId: string) => request<TeamSnapshot>(`/admin/accounts/${accountId}/team`),

    invite: (accountId: string, input: InviteMemberInput) =>
      requestWithDispatch<{ id: number; email: string; role: string; expiresAt: string }>(
        `/admin/accounts/${accountId}/invitations`,
        { method: 'POST', body: JSON.stringify(input) },
      ),

    /** Extends the expiry by seven days and sends a fresh link. */
    resendInvitation: (accountId: string, invitationId: number) =>
      requestWithDispatch<{ id: number; expiresAt: string }>(
        `/admin/accounts/${accountId}/invitations/${invitationId}/resend`,
        { method: 'POST' },
      ),

    revokeInvitation: (accountId: string, invitationId: number) =>
      request<{ deleted: boolean }>(`/admin/accounts/${accountId}/invitations/${invitationId}`, {
        method: 'DELETE',
      }),

    /**
     * Turn an invitation into a membership for the signed-in user.
     *
     * No account id: the token names the account, and the invitee is not yet a
     * member of anything. The API matches the invitation address against the
     * session's own verified email.
     */
    accept: (token: string) =>
      request<{ accountId: string }>('/admin/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
  },
};

/* ============================================================
 * Metrc packages, and assigning them to variants.
 *
 * On the API because `assign_metrc_package` / `reassign_metrc_package` are
 * granted to `service_role` alone: they call `inventory_move()`, which writes
 * the stock ledger and the running total in one place so the two cannot drift.
 * See `apps/api/src/routes/metrc-packages.ts`.
 * ============================================================ */

/** What a package has been assigned to, once it has been. */
export interface MetrcAssignment {
  packageId: string;
  productId: string | null;
  productName: string | null;
  productImageUrl: string | null;
  variantId: string | null;
  variantName: string | null;
  variantWeightGrams: number | null;
  /** Sellable units still in the lot. */
  units: number;
  status: string;
  unitCostCents: number | null;
  expirationDate: string | null;
  /** Snapshotted at assignment; what a sale is reported to Metrc in. */
  gramsPerUnit: number | null;
}

export interface MetrcPackage {
  id: string;
  label: string;
  metrcId: number;
  quantity: number | null;
  unitOfMeasureName: string | null;
  itemName: string | null;
  itemCategory: string | null;
  strainName: string | null;
  packagedDate: string | null;
  expirationDate: string | null;
  labTestingState: string | null;
  listType: string | null;
  licenseNumber: string;
  /** The verbatim Metrc record; the conversion reads QuantityType from it. */
  payload: Record<string, unknown>;
  assignment: MetrcAssignment | null;
}

export interface CannabisVariantOption {
  variantId: string;
  variantName: string;
  weightGrams: number | null;
  unit: string;
  priceCents: number;
  productId: string;
  productName: string;
  productImageUrl: string | null;
}

export async function listMetrcPackages(
  accountId: string,
  options: { search?: string; assigned?: boolean; limit?: number; offset?: number } = {},
): Promise<{ data: MetrcPackage[]; total: number }> {
  const params = new URLSearchParams();

  if (options.search) params.set('search', options.search);
  if (options.assigned !== undefined) params.set('assigned', String(options.assigned));
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));

  const body = await send(
    `/admin/accounts/${accountId}/metrc-packages${params.size ? `?${params}` : ''}`,
  );

  return { data: (body.data ?? []) as MetrcPackage[], total: Number(body.total ?? 0) };
}

export function getMetrcPackage(accountId: string, metrcPackageId: string): Promise<MetrcPackage> {
  return request<MetrcPackage>(`/admin/accounts/${accountId}/metrc-packages/${metrcPackageId}`);
}

/** Cannabis variants only -- a tagged lot cannot be assigned to an accessory. */
export function searchCannabisVariants(
  accountId: string,
  search: string,
): Promise<CannabisVariantOption[]> {
  return request<CannabisVariantOption[]>(
    `/admin/accounts/${accountId}/metrc-packages/variants?search=${encodeURIComponent(search)}`,
  );
}

export function assignMetrcPackage(
  accountId: string,
  metrcPackageId: string,
  input: {
    variantId: string;
    locationId: string;
    unitCostCents?: number | null;
    expirationDate?: string | null;
  },
): Promise<MetrcPackage> {
  return request<MetrcPackage>(
    `/admin/accounts/${accountId}/metrc-packages/${metrcPackageId}/assign`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

/**
 * Points an assigned package at a different variant.
 *
 * Only what remains moves: units already sold are gone, and the product left
 * in the lot is what converts. Eight 3.5 g units with three sold is 17.5 g,
 * which is two 7 g units -- not four.
 */
export function reassignMetrcPackage(
  accountId: string,
  metrcPackageId: string,
  input: { packageId: string; variantId: string },
): Promise<MetrcPackage> {
  return request<MetrcPackage>(
    `/admin/accounts/${accountId}/metrc-packages/${metrcPackageId}/reassign`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}
